(function () {
  'use strict';

  const names = ['baseStep', 'frameStep', 'widthLengthStep', 'bedConfigFeetStep', 'fabricStep',
    'bedConfigHeadboardStep', 'bedConfigStorageStep', 'bedConfigFootboardStep',
    'configuratorMattressStep', 'configuratorStepTopper', 'configuratorStepUpgrades'];
  const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
  const clone = value => JSON.parse(JSON.stringify(value));
  const fail = field => { throw new Error(`Saved configuration has an unsupported ${field}.`); };
  const requireValue = (value, field) => { if (!value) fail(field); return value; };
  const size = value => /^(\d+)(?:\s*cm)?$/.exec(String(value).trim())?.[1];
  const sameId = (a, b) => a != null && b != null && String(a) === String(b);

  // Read the catalogs actually mounted by this product/template, including
  // preview overrides. No duplicate list of Shopify IDs lives in this helper.
  function collectComponents(root) {
    const components = {};
    for (const element of root.querySelectorAll('[x-data]')) {
      const name = element.getAttribute('x-data');
      if (names.includes(name)) components[name] = window.Alpine.$data(element);
    }
    return components;
  }

  function prepare(value, {defaults, current, components, migrate}) {
    if (!object(value)) fail('selection');
    // These fields existed before the optional additions/migrations below.
    // An empty/partial server record must not silently become a different bed.
    for (const field of ['width', 'length', 'fabric', 'color']) {
      if (value[field] == null) fail(field);
    }
    const nested = ['headboard', 'storage', 'footboard', 'mattress', 'topper', 'upgrades', 'variants'];
    for (const field of nested) {
      if (Object.hasOwn(value, field) && !object(value[field])) fail(field);
    }
    if (Object.hasOwn(value, 'hasFeet') && typeof value.hasFeet !== 'boolean') fail('feet');
    for (const [parent, field] of [['topper', 'option_variant_ids'], ['mattress', 'hardness']]) {
      if (value[parent]?.[field] !== undefined && !Array.isArray(value[parent][field])) fail(`${parent} ${field}`);
    }

    const migrated = migrate(clone(value));
    const result = {...clone(defaults), ...clone(current)};
    for (const field of ['base', 'frame', 'width', 'length', 'feet', 'feetHeight', 'underBedLed', 'fabric', 'color']) {
      if (Object.hasOwn(migrated, field)) result[field] = migrated[field];
    }
    for (const field of nested.filter(field => field !== 'variants')) {
      if (result[field] !== undefined || migrated[field] !== undefined) {
        result[field] = {...result[field], ...migrated[field]};
      }
    }
    for (const [parent, fields] of [[result, ['underBedLed']], [result.headboard, ['backLed', 'frontLed', 'usb']],
      [result.storage, ['light']], [result.footboard, ['led']]]) {
      for (const field of fields) if (parent && typeof parent[field] !== 'boolean') fail(field);
    }
    if (typeof result.fabric !== 'string' || typeof result.color !== 'string') fail('fabric/color');

    // Serialized variant buckets are derived caches, not authority. Clearing
    // them prevents retired IDs from hiding otherwise valid restored choices;
    // each mounted component rebuilds its own bucket after store replacement.
    result.variants = Object.fromEntries(Object.keys(defaults.variants || {}).map(key => [key, []]));
    result.all_variant_ids = [];
    result.configLoaded = true;

    const dimensions = requireValue(components.widthLengthStep, 'dimension catalog');
    for (const [field, options] of [['width', dimensions.widthOptions], ['length', dimensions.lengthOptions]]) {
      const number = size(result[field]);
      const selected = requireValue(number && options.find(option => size(option.title) === number), field);
      result[field] = selected.title;
    }
    for (const [name, field] of [['baseStep', 'base'], ['frameStep', 'frame']]) {
      if (components[name]) requireValue(components[name].variants.find(option => option.option1?.handle === result[field]), field);
    }
    const fabric = components.fabricStep;
    if (fabric) requireValue(fabric.variants.find(option =>
      fabric.getFabric(option.option1, option.option1Id)?.handle === result.fabric &&
      fabric.getColor(option.option2, option.option2Id)?.handle === result.color), 'fabric/color combination');

    const feet = components.bedConfigFeetStep;
    if (feet) {
      requireValue(feet.feetOptions.find(option => option.option1?.handle === result.feet), 'feet');
      if (result.feet === 'feet') {
        const selected = requireValue(feet.feetHeightOptions.find(option => size(option.value) === size(result.feetHeight)), 'feet height');
        result.feetHeight = selected.value;
      }
    }
    const headboard = components.bedConfigHeadboardStep;
    if (headboard) {
      requireValue(headboard.headboards.find(option => option.option1?.handle === result.headboard.style), 'headboard');
      const selected = requireValue(headboard.heightOptions.find(option => size(option.title) === size(result.headboard.height)), 'headboard height');
      result.headboard.height = selected.title;
    }
    for (const [name, field] of [['bedConfigStorageStep', 'storage'], ['bedConfigFootboardStep', 'footboard']]) {
      if (components[name]) requireValue(components[name].variants.find(option => option.option1?.handle === result[field]?.style), field);
    }
    const mattress = components.configuratorMattressStep;
    if (mattress) {
      const product = requireValue(mattress.products.find(option => sameId(option.id, result.mattress.product_id)), 'mattress product');
      const variant = requireValue(product.variants.find(option => sameId(option.id, result.mattress.variant_id)), 'mattress variant');
      result.mattress.product_id = product.id;
      result.mattress.variant_id = variant.id;
      const hardness = result.mattress.hardness;
      if (!Array.isArray(hardness) || !hardness.length || hardness.length > 2 ||
          hardness.some(handle => !variant.hardness_options.some(option => option.handle === handle))) fail('mattress firmness');
      // Legacy single firmness applies to both sides when entering split mode.
      const split = product.admin_value?.includes('2x') || (result.frame === 'elektrisch' && Number(size(result.width)) > 140);
      result.mattress.hardness = split ? [hardness[0], hardness[1] ?? hardness[0]] : [hardness[0]];
    }
    const topper = components.configuratorStepTopper;
    if (topper) {
      const dimension = `${size(result.width)}x${size(result.length)}`;
      const variants = topper.variants.filter(option => option.option1 === dimension);
      requireValue(variants.length, 'topper size');
      // Old links may only store the exact variant, or only the product. Never
      // replace an explicitly retired variant with an arbitrary first product.
      const savedTopper = migrated.topper || {};
      const productOnly = savedTopper.product_id != null && savedTopper.variant_id == null;
      const variant = requireValue(!productOnly && result.topper.variant_id != null
        ? variants.find(option => sameId(option.id, result.topper.variant_id))
        : variants.find(option => sameId(option.product_id, result.topper.product_id)), 'topper variant');
      if (savedTopper.product_id != null && !sameId(variant.product_id, savedTopper.product_id)) fail('topper product');
      result.topper.product_id = variant.product_id;
      result.topper.variant_id = variant.id;
      const ids = result.topper.option_variant_ids;
      if (!Array.isArray(ids) || ids.length > 2) fail('topper firmness');
      result.topper.option_variant_ids = ids.map(id => requireValue(topper.topper_options.find(option => sameId(option.id, id)), 'topper firmness').id);
    }
    const upgrades = components.configuratorStepUpgrades;
    if (upgrades) {
      for (const [id, selected] of Object.entries(result.upgrades)) {
        if (selected == null) continue;
        const product = requireValue(upgrades.products.find(option => sameId(option.id, id)), 'upgrade product');
        result.upgrades[id] = requireValue(product.variants.find(option => sameId(option.id, selected)), 'upgrade variant').id;
      }
    }
    return result;
  }

  window.MaisonBedRestore = {collectComponents, prepare};
})();
